import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { constants } from '../../../config';
import { createSelector } from 'reselect';
import { useSelector } from 'react-redux';

// Establece el token de acceso de Mapbox
mapboxgl.accessToken = constants.REACT_APP_MAPBOX;

/**
 * MapCoordinatePicker - Interactive map with draggable marker for coordinate selection
 * @param {number} initialLatitude - Initial latitude for marker position
 * @param {number} initialLongitude - Initial longitude for marker position
 * @param {function} onCoordinatesChange - Callback function when coordinates change: (coords) => void
 */
const MapCoordinatePicker = ({ initialLatitude, initialLongitude, onCoordinatesChange }) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const marker = useRef(null);

    const selectLayoutState = (state) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({ layoutModeType: layout.layoutModeType })
    );
    const { layoutModeType } = useSelector(selectLayoutProperties);

    const mapStyle = layoutModeType === 'dark'
        ? 'mapbox://styles/mapbox/dark-v10'
        : 'mapbox://styles/mapbox/light-v11';

    useEffect(() => {
        // Initialize map only once
        if (map.current) return;

        // Create map instance
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: mapStyle,
            center: [initialLongitude, initialLatitude],
            zoom: 10,
        });

        // Create draggable marker
        marker.current = new mapboxgl.Marker({
            draggable: true,
            color: '#FF5733' // Orange-red color to stand out
        })
            .setLngLat([initialLongitude, initialLatitude])
            .addTo(map.current);

        // Handle marker drag end event
        marker.current.on('dragend', () => {
            const lngLat = marker.current.getLngLat();
            onCoordinatesChange({
                latitude: lngLat.lat,
                longitude: lngLat.lng
            });
        });

        // Handle map click to reposition marker
        map.current.on('click', (e) => {
            const { lng, lat } = e.lngLat;
            marker.current.setLngLat([lng, lat]);
            onCoordinatesChange({
                latitude: lat,
                longitude: lng
            });
        });

        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, []); // Empty dependency array - initialize once

    // Update map style when theme changes
    useEffect(() => {
        if (map.current) {
            map.current.setStyle(mapStyle);
        }
    }, [mapStyle]);

    // Update marker position when initial coordinates change
    useEffect(() => {
        if (marker.current) {
            marker.current.setLngLat([initialLongitude, initialLatitude]);
            if (map.current) {
                map.current.flyTo({
                    center: [initialLongitude, initialLatitude],
                    zoom: 10
                });
            }
        }
    }, [initialLatitude, initialLongitude]);

    return (
        <div className="mb-3">
            <div
                style={{
                    width: '100%',
                    height: 0,
                    paddingBottom: '100%', 
                    position: 'relative',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <div
                    ref={mapContainer}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }}
                />
            </div>
        </div>
    );
};

export default MapCoordinatePicker;
